/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useCallback } from 'react';

import { uniqueId } from 'lodash';

import { breakpoints, useTheme } from '@coursera/cds-core';

import type {
  EntWebsiteBlockContactSalesQuery,
  EntWebsiteContactSalesFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import DividerModule from 'bundles/ent-website/contentful/modules/DividerModule';
import { HowModule } from 'bundles/ent-website/contentful/modules/HowModule';
import { LeaderListModule } from 'bundles/ent-website/contentful/modules/LeaderListModule';
import { MarketoModule } from 'bundles/ent-website/contentful/modules/MarketoModule';
import { PartnerListModule } from 'bundles/ent-website/contentful/modules/PartnerListModule';
import { TestimonialModule } from 'bundles/ent-website/contentful/modules/TestimonialModule';
import { entWebsiteBlockContactSalesQuery } from 'bundles/ent-website/queries/EntWebsiteContactSales';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteContactSalesFragment>;

export function ContactSalesModule({ content, assembly }: Props) {
  const { marketoModule, modulesCollection, hubspotCodeEmbed } = content ?? {};

  const theme = useTheme();

  const styles = {
    module: css`
      margin-bottom: var(--cds-spacing-600);

      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-200);
      }

      .LeaderListModule-leaderList {
        margin-top: var(--cds-spacing-200);
      }

      .LeaderListModule-headline {
        margin-bottom: var(--cds-spacing-200);
      }
    `,

    form: css`
      font-family: ${theme.typography.fontFamily} !important;
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-grey-200);
      padding: var(--cds-spacing-150) var(--cds-spacing-150);

      ${breakpoints.down('xs')} {
        padding: var(--cds-spacing-100) var(--cds-spacing-100);
      }
    `,
    divider: css`
      ${breakpoints.down('xs')} {
        display: none;
      }
    `,
  };

  const contentPanel = () => (
    <div>
      {modulesCollection?.items?.map((m) => {
        switch (m?.__typename) {
          case 'EntWebsiteDivider':
            return (
              <div css={[styles.module, styles.divider]} key={`${m.__typename}:${uniqueId()}`}>
                <DividerModule content={m} />
              </div>
            );

          case 'EntWebsiteHow':
            return (
              <div css={styles.module} key={`${m.__typename}:${m.slug}`}>
                <HowModule
                  content={m}
                  assembly={{
                    layout: 'no-image',
                    theme: assembly.theme,
                    visibility: {
                      headline: true,
                      description: true,
                      howDetails: true,
                    },
                    layoutOptions: ['inner-layout'],
                  }}
                />
              </div>
            );

          case 'EntWebsitePartnerList':
            return (
              <div css={styles.module} key={`${m.__typename}:${m.slug}`}>
                <PartnerListModule
                  content={m}
                  assembly={{
                    layout: 'text-top/image-bottom',
                    theme: assembly.theme,
                    layoutOptions: ['hide-on-phone', 'inner-layout'],
                    visibility: {},
                  }}
                />
              </div>
            );

          case 'EntWebsiteTestimonial':
            return (
              <div css={styles.module} key={`${m.__typename}:${m.slug}`}>
                <TestimonialModule
                  content={m}
                  assembly={{
                    layout: 'image-top/text-bottom',
                    theme: assembly.theme,
                    layoutOptions: ['hide-on-phone', 'inner-layout'],
                    visibility: {
                      headline: true,
                      testimonial: true,
                      authorOrganization: true,
                      author: true,
                      authorTitle: true,
                      authorAvatar: true,
                    },
                  }}
                />
              </div>
            );

          case 'EntWebsiteLeaderList':
            return (
              <div css={styles.module} key={`${m.__typename}:${m.slug}`}>
                <LeaderListModule
                  content={m}
                  assembly={{
                    layout: 'image-top/text-bottom',
                    theme: assembly.theme,
                    layoutOptions: ['inner-layout'],
                    visibility: {
                      headline: true,
                      description: true,
                      leaders: true,
                    },
                  }}
                />
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );

  const formPanel = useCallback<() => $TSFixMe>(() => {
    // eslint-disable-next-line react/no-danger
    if (hubspotCodeEmbed) return <div dangerouslySetInnerHTML={{ __html: `${hubspotCodeEmbed}` }} />;

    if (marketoModule) {
      return (
        <div css={styles.form}>
          <MarketoModule
            content={marketoModule}
            assembly={{ layout: 'no-image', theme: assembly.theme, layoutOptions: ['inner-layout'] }}
          />
        </div>
      );
    }
  }, [assembly.theme, hubspotCodeEmbed, marketoModule, styles.form]);

  return <BlockLayout assembly={assembly} renderContent={contentPanel} renderImage={formPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockContactSalesModule = withBlockQuery<EntWebsiteContactSalesFragment, EntWebsiteBlockContactSalesQuery>(
  entWebsiteBlockContactSalesQuery
)(ContactSalesModule);
